import { useState, useEffect, useCallback } from 'react';
import { fetchWithTimeout } from '../lib/fetchWithTimeout';

export interface DocketFiler {
  filer_name: string;
  submission_ids: string[];
  filing_count: number;
}

interface UseDocketFilersResult {
  filers: DocketFiler[];
  isLoading: boolean;
  error: string | null;
}

function getBackendBaseUrl(): string {
  const raw = (import.meta as any)?.env?.VITE_RAG_API_BASE_URL as
    | string
    | undefined;
  const fallback = 'https://fcc-summarize-app-r4ac3.ondigitalocean.app';
  return (raw || fallback).replace(/\/+$/, '');
}

/**
 * Fetches the list of filers for a docket from GET /docket/{docket_number}/filers.
 * Returns { filers, isLoading, error }.
 * Each filer has: filer_name, submission_ids, filing_count.
 */
export function useDocketFilers(docketNumber: string | null): UseDocketFilersResult {
  const [filers, setFilers] = useState<DocketFiler[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFilers = useCallback(async () => {
    if (!docketNumber) return;

    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = `${getBackendBaseUrl()}/docket/${encodeURIComponent(docketNumber)}/filers`;
      const response = await fetchWithTimeout(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch filers: ${response.status}`);
      }

      const data = await response.json();
      // Support both { filers: [...] } and a bare array response
      const filerList: DocketFiler[] = Array.isArray(data) ? data : (data.filers ?? []);
      setFilers(filerList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch filers');
      setFilers([]);
    } finally {
      setIsLoading(false);
    }
  }, [docketNumber]);

  useEffect(() => {
    if (docketNumber) {
      fetchFilers();
    } else {
      setFilers([]);
      setError(null);
    }
  }, [docketNumber, fetchFilers]);

  return { filers, isLoading, error };
}
